import { Navigate, useParams, useSearchParams, Link } from "react-router-dom";
import { CommissionWizard } from "@/features/commission/components/CommissionWizard";
import { ArtistStep } from "@/features/commission/components/steps/ArtistStep";
import { useIntakeArtist } from "@/features/commission/hooks/useIntakeArtist";
import { useIntakeCopy } from "@/features/commission/i18n/useIntakeCopy";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { Button, buttonVariants } from "@/components/ui/button";
import { usePageMeta } from "@/hooks";

export function CommissionPage() {
  const { artistSlug } = useParams();
  const [params] = useSearchParams();
  const legacy = params.get("artist");
  if (!artistSlug && legacy) return <Navigate to={`/commission/${encodeURIComponent(legacy)}`} replace />;
  return artistSlug ? <ArtistCommission slug={artistSlug} /> : <PickArtist />;
}

function PickArtist() {
  usePageMeta({
    title: "Start a Commission | StudioComm",
    description: "Request custom hand-drawn art from StudioComm artists. Pick your artist, style and options, and get a price estimate instantly.",
    canonicalPath: "/commission",
  });
  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6 sm:p-8"><ArtistStep /></div>
    </div>
  );
}

function ArtistCommission({ slug }: { slug: string }) {
  const { c, fmt } = useIntakeCopy();
  const { data: artist, isLoading, error, refetch } = useIntakeArtist(slug);
  usePageMeta({
    title: fmt(c.meta.title, { artist: artist?.name ?? "" }),
    description: fmt(c.meta.description, { artist: artist?.name ?? "" }),
    canonicalPath: `/commission/${slug}`,
  });
  if (isLoading) return <div className="flex min-h-[60vh] items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (error?.status === 404 || error?.status === 400) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="text-2xl font-semibold text-[var(--color-text)]">{c.notFound.title}</h1>
        <p className="mt-2 text-[var(--color-text-muted)]">{c.notFound.body}</p>
        <Link to="/commission" className={buttonVariants({ className: "mt-6" })}>{c.notFound.cta}</Link>
      </div>
    );
  }
  if (error || !artist) {
    return (
      <div data-prerender="skip" className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="text-xl text-[var(--color-text)]">{c.loadError.title}</h1>
        <Button className="mt-6" onClick={() => refetch()}>{c.loadError.retry}</Button>
      </div>
    );
  }
  return <CommissionWizard artist={artist} />;
}
